// failure.service.ts — why an ask produced no answer, and whether to offer Retry.
//
// ─── THE ONE BUG THIS FILE EXISTS TO PREVENT ───────────────────────────────
// The shared api-client reports an EXTERNALLY ABORTED request and a genuinely
// dead network as the same value. Both are
//
//     ApiError { kind: 'network', message: … }
//
// (`createApiClient.ts` sets `message: 'Request was cancelled'` for the abort,
// but that string is a human message, not a contract — keying UI behaviour off
// its exact wording is a defect waiting for someone to reword it.) So there is
// NOTHING in the error object that distinguishes "the operator pressed Stop"
// from "the wifi died".
//
// The consequence, if this file gets it wrong: pressing Stop renders "Check your
// connection". That is a lie the type system cannot catch, on an operator-facing
// surface, about the state of their own machine — and it sends them debugging
// their network instead of re-asking their question.
//
// The fix is that the caller records `cancelledByUser` BEFORE calling `abort()`
// and passes it in. `toAiFailure` consults that flag, not the message. Pinned by
// tests below and in `__tests__/failure.service.test.ts`.
//
// ─── WHY `auth` MAPS TO `unavailable` AND NOT TO A SESSION PROBLEM ─────────
// The chatbot host has no authentication at all — `tenant_id` travels in the
// request BODY (see `schemas/chat.schema.ts`) precisely because there is no token
// to carry it. So a 401/403 from that hostname cannot be an expired session; it
// is an ingress, WAF or gateway refusing the call before the app sees it.
// Rendering "your session expired" would send the operator to re-login, which
// would change nothing and would make them distrust every other panel that IS
// session-backed. "Temporarily unavailable" is the honest reading.
//
// ─── WHY `rejected` GETS NO RETRY BUTTON ───────────────────────────────────
// A 4xx from this host means the body was refused (over-length question, bad
// tenant UUID). An identical retry produces an identical rejection, so a Retry
// button would be a loop with a friendly label. The question goes back into the
// composer to edit instead — that is what `'edit-and-resend'` means.

import { isApiError } from '@fluxora/api-client';

import { CHAT_TIMEOUT_MS } from '../constants';
import type { AiFailure, AiRetryability } from '../types';

/**
 * A thrown value that is not an `ApiError` at all.
 *
 * Reachable in two ways: a bug in our own code between `fetch` and the caller,
 * or an abort raised before the client wrapped it. Because the second is real, an
 * un-cancelled unknown degrades to `offline` (retryable, no blame placed) rather
 * than to `contract` (terminal) — an over-confident terminal verdict would strand
 * a recoverable ask with no way forward.
 */
function unknownFailure(cancelledByUser: boolean): AiFailure {
  return cancelledByUser ? { kind: 'cancelled' } : { kind: 'offline' };
}

/**
 * Classify a thrown value from the chat request.
 *
 * `options.cancelledByUser` must be the flag the caller set BEFORE aborting —
 * see the file header. Passing `false` when the operator did press Stop produces
 * a confidently wrong "check your connection".
 */
export function toAiFailure(
  error: unknown,
  options: { readonly cancelledByUser: boolean },
): AiFailure {
  if (!isApiError(error)) return unknownFailure(options.cancelledByUser);

  switch (error.kind) {
    case 'network':
      // THE FORK. Byte-identical inputs, two different stories on screen, and
      // only the caller's flag can tell them apart.
      return unknownFailure(options.cancelledByUser);

    case 'timeout':
      // Our configured budget, not `error.timeoutMs`: the copy beside this
      // ("no answer in 25 seconds") is written against the constant, so the
      // number the operator reads and the number we waited are the same symbol.
      // The aiChat client MUST therefore be constructed with `CHAT_TIMEOUT_MS`;
      // if it ever is not, these two would disagree.
      return { kind: 'timeout', timeoutMs: CHAT_TIMEOUT_MS };

    case 'server':
      return { kind: 'unavailable', status: error.status };

    case 'auth':
      // Not a session problem. See the file header.
      return { kind: 'unavailable', status: error.status };

    case 'client':
      // The only user-fixable failure: the body was refused, so editing it is
      // the move and Retry is not offered.
      return { kind: 'rejected', status: error.status };

    case 'validation':
      // The response parsed as JSON but not as our schema — the contract moved
      // under us. No amount of retrying fixes that, and it is a defect to report
      // rather than an incident to wait out.
      return { kind: 'contract' };

    default:
      // Unreachable against today's `ApiError` union, and kept anyway: this union
      // lives in Common and a version bump can add a member. Degrading is
      // strictly better than a build break in a country app for a Common change,
      // and better than falling through with no return value.
      return unknownFailure(options.cancelledByUser);
  }
}

/**
 * Whether to offer Retry, send the operator back to the composer, or neither.
 *
 * `cancelled` is retryable on purpose: pressing Stop is a decision to re-ask,
 * not an error, and the fastest path back is the same button.
 */
export function retryabilityOf(failure: AiFailure): AiRetryability {
  switch (failure.kind) {
    case 'rejected':
      // An identical body returns an identical 4xx. Editing is the only exit.
      return 'edit-and-resend';

    case 'contract':
    case 'no-tenant':
      // Both are defects in the system, not conditions that pass. `no-tenant`
      // means the session carried no tenant claim, and asking again as the same
      // principal cannot produce one.
      return 'terminal';

    case 'cancelled':
    case 'offline':
    case 'timeout':
    case 'unavailable':
      return 'retryable';
  }
}
